import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronRight,
  List,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  UserPlus,
} from "lucide-react";
import toast from "react-hot-toast";
import { displayLabel, employeeDisplayName } from "../../../utils/displayLabel";
import { ActionProcessCard } from "./ModuleActionsFrame";

const PAGE_SIZE = 50;

function empCodeOf(e) {
  return e?.empCode || e?.employeeCode || e?.code || String(e?.id ?? e?.employeeId ?? "");
}

function empNameOf(e) {
  return (
    employeeDisplayName(e) ||
    e?.user?.name ||
    e?.name ||
    e?.fullName ||
    [e?.firstName, e?.lastName].filter(Boolean).join(" ").trim() ||
    "—"
  );
}

function designationOf(e) {
  return (
    displayLabel(e?.designation?.name || e?.designation || e?.Designation?.name, "") ||
    "—"
  );
}

function departmentOf(e) {
  return (
    displayLabel(e?.department?.name || e?.department || e?.Department?.name, "") ||
    "—"
  );
}

function statusOf(e) {
  return displayLabel(
    e?.status || e?.employmentStatus || e?.user?.status,
    "—"
  );
}

function toOptions(list = []) {
  const seen = new Set();
  return (list || [])
    .filter((e) => e && (e.id != null || e.employeeId != null))
    .filter((e) => {
      const id = String(e.id ?? e.employeeId);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map((e) => {
      const id = e.id ?? e.employeeId;
      const code = empCodeOf(e);
      const name = empNameOf(e);
      const label = [code, name].filter(Boolean).join(" - ");
      return { value: id, label: label || String(id) };
    })
    .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { numeric: true }));
}

/**
 * Shared Actions body for Employee / Attendance.
 * Process = pick any employee from directory.
 * List = actioned records only (from listActionRecords), same as Payroll.
 */
export default function ModuleActionEmployeePanel({
  darkMode = false,
  actionKey,
  title,
  blurb,
  processHint = "",
  listTitle = "Actioned list",
  emptyList = "No actioned employees yet for this action.",
  openLabel = "Open",
  Icon3d = null,
  employees = [],
  loadingEmployees = false,
  listActionRecords = null,
  onOpenEmployee,
  onRefreshEmployees = null,
}) {
  const [panel, setPanel] = useState("process");
  const [employeeId, setEmployeeId] = useState("");
  const [listQ, setListQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [actionedRows, setActionedRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const border = "border-[hsl(var(--ds-primary)/0.35)]";
  const muted = "text-[hsl(var(--ds-muted-fg))]";

  useEffect(() => {
    setEmployeeId("");
    setPanel("process");
    setListQ("");
    setStatusFilter("all");
    setVisibleCount(PAGE_SIZE);
    setActionedRows([]);
  }, [actionKey]);

  const loadList = useCallback(async () => {
    if (typeof listActionRecords !== "function" || !actionKey) {
      setActionedRows([]);
      return;
    }
    setLoadingList(true);
    try {
      const res = await listActionRecords(actionKey, {
        q: listQ.trim() || undefined,
        limit: 500,
      });
      setActionedRows(Array.isArray(res?.data) ? res.data : []);
      setVisibleCount(PAGE_SIZE);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load actioned list");
      setActionedRows([]);
    } finally {
      setLoadingList(false);
    }
  }, [actionKey, listActionRecords, listQ]);

  useEffect(() => {
    if (panel !== "list") return undefined;
    const t = setTimeout(() => {
      loadList();
    }, listQ ? 280 : 0);
    return () => clearTimeout(t);
  }, [panel, loadList, listQ]);

  const options = useMemo(() => toOptions(employees), [employees]);

  const statuses = useMemo(() => {
    const set = new Set();
    (actionedRows || []).forEach((e) => {
      const s = statusOf(e);
      if (s && s !== "—") set.add(s);
    });
    return ["all", ...[...set].sort()];
  }, [actionedRows]);

  const filteredRows = useMemo(() => {
    if (statusFilter === "all") return actionedRows;
    return (actionedRows || []).filter((e) => statusOf(e) === statusFilter);
  }, [actionedRows, statusFilter]);

  const visibleRows = useMemo(
    () => filteredRows.slice(0, visibleCount),
    [filteredRows, visibleCount]
  );
  const hasMore = filteredRows.length > visibleCount;

  const openEmp = async (row) => {
    const id = Number(row?.employeeId || row?.id);
    if (!id) {
      toast.error("Employee not found");
      return;
    }
    let emp =
      (employees || []).find((e) => String(e.id) === String(id)) || {
        id,
        employeeId: id,
        empCode: row.empCode,
        name: row.name,
        designation: row.designation,
        department: row.department,
        employmentStatus: row.employmentStatus,
        ...row,
      };
    if (typeof onOpenEmployee === "function") onOpenEmployee(emp);
  };

  const handleProcess = () => {
    if (!employeeId) {
      toast.error("Select an employee first");
      return;
    }
    const emp = (employees || []).find((e) => String(e.id) === String(employeeId));
    openEmp(emp || { id: Number(employeeId), employeeId: Number(employeeId) });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg bg-[hsl(var(--ds-primary)/0.08)] p-1 pace-action-rail-icon">
            {Icon3d ? <Icon3d size={24} /> : null}
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold tracking-tight text-[hsl(var(--ds-fg))]">
              {title}
            </h2>
            {blurb ? <p className={`text-[11px] ${muted}`}>{blurb}</p> : null}
          </div>
        </div>

        <div
          className={`inline-flex rounded-lg border ${border} bg-white p-0.5 dark:bg-[hsl(var(--ds-surface))]`}
        >
          <button
            type="button"
            onClick={() => setPanel("process")}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
              panel === "process"
                ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
            }`}
          >
            <UserPlus size={14} /> Process
          </button>
          <button
            type="button"
            onClick={() => setPanel("list")}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
              panel === "list"
                ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                : "text-[hsl(var(--ds-fg))] hover:text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
            }`}
          >
            <List size={14} /> {listTitle}
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {panel === "process" ? (
          <ActionProcessCard
            darkMode={darkMode}
            title="Select employee"
            infoText={processHint}
            selectId={`module-action-${actionKey}`}
            employeeId={employeeId}
            onEmployeeChange={(e) => setEmployeeId(String(e?.target?.value ?? ""))}
            employeeOptions={options}
            loading={loadingEmployees}
            primaryLabel={openLabel}
            onPrimary={handleProcess}
            showSecondary
            secondaryLabel="View list"
            onSecondary={() => setPanel("list")}
          />
        ) : (
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="relative min-w-[14rem] flex-1">
                <Search
                  size={13}
                  className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`}
                />
                <input
                  type="search"
                  value={listQ}
                  onChange={(e) => {
                    setListQ(e.target.value);
                    setVisibleCount(PAGE_SIZE);
                  }}
                  placeholder="Search code, name, dept…"
                  className={`h-9 w-full rounded-lg border bg-[hsl(var(--ds-surface))] pl-8 pr-3 text-xs ${border}`}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setVisibleCount(PAGE_SIZE);
                }}
                className={`h-9 rounded-lg border bg-[hsl(var(--ds-surface))] px-2 text-xs ${border}`}
                aria-label="Status filter"
              >
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s === "all" ? "All statuses" : s}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={loadList}
                className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold ${border} hover:bg-[hsl(var(--ds-muted))]`}
              >
                <RefreshCw size={13} /> Refresh
              </button>
              <button
                type="button"
                onClick={() => setPanel("process")}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[hsl(var(--ds-primary))] px-3 text-xs font-semibold text-[hsl(var(--ds-primary-fg))]"
              >
                <Plus size={13} /> New
              </button>
            </div>

            <div
              className={`relative min-h-0 flex-1 overflow-auto rounded-lg border bg-[hsl(var(--ds-surface))] ${border}`}
            >
              <table className="min-w-full text-left text-xs">
                <thead className="sticky top-0 z-[1] bg-[hsl(var(--ds-primary)/0.08)]">
                  <tr className="border-b border-[hsl(var(--ds-primary)/0.2)]">
                    <th className="px-3 py-2.5 font-semibold">Sl No</th>
                    <th className="px-3 py-2.5 font-semibold">Emp Code</th>
                    <th className="px-3 py-2.5 font-semibold">Name</th>
                    <th className="px-3 py-2.5 font-semibold">Designation</th>
                    <th className="px-3 py-2.5 font-semibold">Department</th>
                    <th className="px-3 py-2.5 font-semibold">Status</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingList ? (
                    <tr>
                      <td colSpan={7} className={`px-3 py-12 text-center ${muted}`}>
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin" /> Loading actioned list…
                        </span>
                      </td>
                    </tr>
                  ) : filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={`px-3 py-12 text-center ${muted}`}>
                        <p className="font-medium text-[hsl(var(--ds-fg))]">{emptyList}</p>
                        <p className="mt-1 text-[11px]">
                          Use Process to select an employee and run this action. Actioned employees appear here.
                        </p>
                        <button
                          type="button"
                          onClick={() => setPanel("process")}
                          className={`mt-3 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${border} hover:bg-[hsl(var(--ds-muted))]`}
                        >
                          <UserPlus size={13} /> Process new
                        </button>
                      </td>
                    </tr>
                  ) : (
                    visibleRows.map((row, idx) => (
                      <tr
                        key={`${row.employeeId || row.id}-${idx}`}
                        className="border-b border-[hsl(var(--ds-primary)/0.1)] last:border-0 hover:bg-[hsl(var(--ds-primary)/0.04)]"
                      >
                        <td className="px-3 py-2.5 tabular-nums">{idx + 1}</td>
                        <td className="px-3 py-2.5 font-semibold">{empCodeOf(row)}</td>
                        <td className="px-3 py-2.5">{empNameOf(row)}</td>
                        <td className="px-3 py-2.5">{designationOf(row)}</td>
                        <td className="px-3 py-2.5">{departmentOf(row)}</td>
                        <td className="px-3 py-2.5">{statusOf(row)}</td>
                        <td className="px-3 py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => openEmp(row)}
                            className="inline-flex items-center gap-1 rounded-lg bg-[hsl(var(--ds-primary))] px-2.5 py-1.5 text-[11px] font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"
                          >
                            {openLabel} <ChevronRight size={12} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {!loadingList && filteredRows.length > 0 ? (
              <div className="relative mt-2 flex shrink-0 items-center justify-between gap-2">
                <p className={`text-[11px] ${muted}`}>
                  Showing {visibleRows.length} of {filteredRows.length} actioned employee
                  {filteredRows.length === 1 ? "" : "s"}
                </p>
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--ds-primary)/0.4)] bg-white px-3.5 py-1.5 text-[11px] font-semibold text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.06)] dark:bg-[hsl(var(--ds-surface))]"
                  >
                    Load more
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
